import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { marqueeTiming } from '@/lib/marquee'
import { cn } from '@/lib/utils'

type ScrollingNameProps = {
  children: ReactNode
  /** Tipografia e layout del contenitore (es. `flex-1 text-right font-display`): lo spazio lo decide il genitore. */
  className?: string
  /** Testo completo al passaggio del mouse; di default il contenuto se è una stringa. */
  title?: string
}

/**
 * Nome (squadra, partita) che non deve mai allargare la riga: se ci sta resta fermo, se è più lungo del suo
 * spazio scorre avanti e indietro quanto basta per leggerlo tutto, con una pausa ai due capi. Si rimisura a
 * ogni cambio di dimensione (rotazione, font caricato, punteggio che cambia larghezza). Con "riduci
 * movimento" torna il taglio con i puntini.
 */
export function ScrollingName({ children, className, title }: ScrollingNameProps) {
  const outerRef = useRef<HTMLSpanElement>(null)
  const innerRef = useRef<HTMLSpanElement>(null)
  const isReduced = useReducedMotion()
  const [timing, setTiming] = useState<{ shift: number; duration: number } | null>(null)

  useLayoutEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (isReduced || !outer || !inner) return
    const measure = () => {
      const next = marqueeTiming(outer.clientWidth, inner.offsetWidth)
      // Solo se cambia davvero: un nuovo oggetto a ogni misura ridisegnerebbe (e riavvierebbe l'animazione) di continuo.
      setTiming((current) => (current?.shift === next?.shift && current?.duration === next?.duration ? current : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [isReduced])

  const fullTitle = title ?? (typeof children === 'string' ? children : undefined)
  if (isReduced) {
    return (
      <span title={fullTitle} className={cn('block min-w-0 truncate', className)}>
        {children}
      </span>
    )
  }

  const style = timing ? ({ '--name-scroll-shift': `${timing.shift}px`, '--name-scroll-duration': `${timing.duration}s` } as CSSProperties) : undefined
  return (
    // Mentre scorre il testo parte da sinistra: allineato a destra, l'eccedenza sparirebbe dal lato sbagliato.
    <span ref={outerRef} title={fullTitle} className={cn('block min-w-0 overflow-hidden whitespace-nowrap', className, timing && 'text-left')}>
      <span ref={innerRef} style={style} className={cn('inline-block', timing && 'name-scroll')}>
        {children}
      </span>
    </span>
  )
}
